import { useEffect, useState } from 'react';

import type { UnifiedProfileFlagsResponse } from '../pages/api/feature-flags/unified-profile';

const DEFAULT_FLAGS: UnifiedProfileFlagsResponse = { phase1: 'off', phase2: 'off' };

// Flags don't change mid-session, so one fetch serves every layout mount and
// client-side navigation without flicker.
let cachedFlags: UnifiedProfileFlagsResponse | null = null;
let pendingFetch: Promise<UnifiedProfileFlagsResponse> | null = null;

async function fetchUnifiedProfileFlags(): Promise<UnifiedProfileFlagsResponse> {
  const response = await fetch('/api/feature-flags/unified-profile');
  if (!response.ok) {
    throw new Error(`Failed to fetch unified profile flags: ${response.status}`);
  }
  return response.json();
}

/**
 * Unified-profile rollout flags, evaluated server-side via
 * `/api/feature-flags/unified-profile`. Returns `'off'` values until the
 * response arrives (and on error), so consumers fail safe to current behavior.
 */
export function useUnifiedProfileFlags(): UnifiedProfileFlagsResponse {
  const [flags, setFlags] = useState<UnifiedProfileFlagsResponse>(cachedFlags ?? DEFAULT_FLAGS);

  useEffect(() => {
    if (cachedFlags) {
      return;
    }

    let cancelled = false;
    pendingFetch ??= fetchUnifiedProfileFlags();
    pendingFetch
      .then((fetched) => {
        cachedFlags = fetched;
        if (!cancelled) {
          setFlags(fetched);
        }
      })
      .catch(() => {
        // Allow a later mount to retry; consumers keep the safe defaults.
        pendingFetch = null;
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return flags;
}
